import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { Sheet, SheetContent, SheetHeader, SheetTitle } from "@/components/ui/sheet";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { toast } from "sonner";
import {
  Pencil, Trash2, Lock, Unlock, X, Mail, Phone, MapPin,
  Clock, Plane, HeartPulse, ShieldCheck,
} from "lucide-react";

const roleBadge = (role) => ({
  super_admin: "bg-violet-100 text-violet-800",
  admin: "bg-indigo-100 text-indigo-800",
  manager: "bg-blue-100 text-blue-800",
  employee: "bg-slate-100 text-slate-800",
  kiosk: "bg-amber-100 text-amber-800",
}[role] || "bg-slate-100 text-slate-800");

/**
 * Reusable slide-out drawer showing full employee detail (Info, Vertrag, Soll, Zeiten, Urlaub).
 * Used from UsersPage AND PersonnelPage / SchedulePage – one click on a name opens the same view.
 */
export default function UserDetailSheet({ userId, open, onOpenChange, locations = [], onEdit, onChanged }) {
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(false);
  const navigate = useNavigate();

  const handleEdit = (u) => {
    if (onEdit) { onEdit(u); return; }
    // Default: navigate to /users with edit query — UsersPage auto-opens the edit dialog
    onOpenChange(false);
    navigate(`/users?edit=${u.id}`);
  };

  useEffect(() => {
    if (!open || !userId) { setDetail(null); return; }
    let alive = true;
    (async () => {
      setLoading(true);
      try {
        const { data } = await api.get(`/personnel/user/${userId}/summary`);
        if (alive) setDetail(data);
      } catch (err) {
        toast.error("Details konnten nicht geladen werden");
      } finally {
        if (alive) setLoading(false);
      }
    })();
    return () => { alive = false; };
  }, [userId, open]);

  const locName = (id) => locations.find(l => l.id === id)?.name || "—";

  const toggleActive = async () => {
    if (!detail?.user) return;
    const u = detail.user;
    if (!window.confirm(`Mitarbeiter wirklich ${u.active ? "sperren" : "entsperren"}?`)) return;
    try {
      await api.post(`/users/${u.id}/toggle-active`);
      toast.success(u.active ? "Gesperrt" : "Entsperrt");
      const { data } = await api.get(`/personnel/user/${u.id}/summary`);
      setDetail(data);
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  const remove = async () => {
    if (!detail?.user) return;
    if (!window.confirm(`${detail.user.first_name} ${detail.user.last_name} wirklich löschen?`)) return;
    try {
      await api.delete(`/users/${detail.user.id}`);
      toast.success("Gelöscht");
      onOpenChange(false);
      onChanged?.();
    } catch (err) { toast.error(err.response?.data?.detail || "Fehler"); }
  };

  return (
    <Sheet open={open} onOpenChange={onOpenChange}>
      <SheetContent side="right" className="w-full sm:max-w-xl overflow-y-auto p-0">
        <SheetHeader className="p-4 border-b border-border/60">
          <SheetTitle>Mitarbeiter-Details</SheetTitle>
        </SheetHeader>
        {loading && !detail ? (
          <div className="p-6 text-sm text-slate-500 text-center">Lädt…</div>
        ) : detail ? (
          <div className="p-4">
            <Body detail={detail} locName={locName} onEdit={() => handleEdit(detail.user)} onToggleActive={toggleActive} onDelete={remove} />
          </div>
        ) : (
          <div className="p-6 text-sm text-slate-500 text-center">Kein Mitarbeiter ausgewählt.</div>
        )}
      </SheetContent>
    </Sheet>
  );
}

function Body({ detail, locName, onEdit, onToggleActive, onDelete }) {
  const u = detail.user;
  const s = detail.stats;
  return (
    <Card className="border-0 shadow-none" data-testid="user-detail-pane">
      <CardContent className="p-0">
        <div className="flex items-start gap-4">
          <Avatar className="w-16 h-16">
            {u.avatar_b64 && <AvatarImage src={u.avatar_b64} />}
            <AvatarFallback className="bg-blue-100 text-blue-700 font-bold text-lg">
              {(u.first_name?.[0] || "?") + (u.last_name?.[0] || "")}
            </AvatarFallback>
          </Avatar>
          <div className="flex-1 min-w-0">
            <div className="font-heading font-bold text-lg leading-tight">{u.academic_title ? `${u.academic_title} ` : ""}{u.first_name} {u.last_name}</div>
            <div className="text-xs text-slate-500 mt-0.5">{u.position || "—"} · {u.username}</div>
            <div className="flex flex-wrap gap-1 mt-2">
              <Badge className={roleBadge(u.role)}>{u.role.replace("_", " ")}</Badge>
              {u.active ? <Badge className="bg-emerald-100 text-emerald-800">Aktiv</Badge> : <Badge variant="secondary">Gesperrt</Badge>}
              <Badge variant="outline">{u.time_tracking_mode}</Badge>
            </div>
          </div>
        </div>

        <div className="grid grid-cols-2 gap-2 mt-4">
          <Stat icon={Clock} label="Diesen Monat" value={`${s.month_hours.toFixed(1)}h`} sub={s.month_overtime > 0 ? `+${s.month_overtime.toFixed(1)}h Ü.` : null} tone="blue" />
          <Stat icon={Plane} label={`Urlaub ${new Date().getFullYear()}`} value={`${s.vacation_days_year} / ${u.vacation_days_per_year}`} sub={`Rest: ${s.vacation_remaining}`} tone="sky" />
          <Stat icon={HeartPulse} label="Kranktage" value={s.sick_days_year} tone="rose" />
          <Stat icon={ShieldCheck} label="Wochensoll" value={`${u.weekly_hours}h`} sub={`Puffer ${u.time_buffer_minutes}m`} tone="emerald" />
        </div>

        <Tabs defaultValue="info" className="mt-5">
          <TabsList className="grid grid-cols-5 w-full">
            <TabsTrigger value="info" data-testid="detail-tab-info">Info</TabsTrigger>
            <TabsTrigger value="vertrag" data-testid="detail-tab-vertrag">Vertrag</TabsTrigger>
            <TabsTrigger value="hours" data-testid="detail-tab-hours">Soll</TabsTrigger>
            <TabsTrigger value="entries" data-testid="detail-tab-entries">Zeiten</TabsTrigger>
            <TabsTrigger value="absences" data-testid="detail-tab-absences">Urlaub</TabsTrigger>
          </TabsList>
          <TabsContent value="info" className="pt-3 space-y-2 text-sm">
            {u.birthday && <InfoRow icon={Plane} label="Geburtstag" value={new Date(u.birthday).toLocaleDateString("de-DE")} />}
            <InfoRow icon={Mail} label="E-Mail" value={u.email || "—"} />
            {u.phone && <InfoRow icon={Phone} label="Telefon" value={u.phone} />}
            {u.mobile && <InfoRow icon={Phone} label="Mobil" value={u.mobile} />}
            {(u.street || u.city) && <InfoRow icon={MapPin} label="Adresse" value={`${u.street || ""} ${u.house_number || ""}, ${u.zip_code || ""} ${u.city || ""}`.trim()} />}
            <InfoRow icon={MapPin} label="Hauptstandort" value={locName(u.primary_location_id)} />
            <InfoRow icon={MapPin} label="Weitere Standorte" value={(u.location_ids || []).filter(id => id !== u.primary_location_id).map(locName).join(", ") || "—"} />
            {u.personnel_number && <InfoRow icon={ShieldCheck} label="Personalnummer" value={u.personnel_number} />}
          </TabsContent>
          <TabsContent value="vertrag" className="pt-3 space-y-2 text-sm">
            <InfoRow icon={Clock} label="Vertragsbeginn" value={u.contract_start ? new Date(u.contract_start).toLocaleDateString("de-DE") : "—"} />
            {u.contract_end && <InfoRow icon={Clock} label="Vertragsende" value={new Date(u.contract_end).toLocaleDateString("de-DE")} />}
            <InfoRow icon={Clock} label="Wochenstunden" value={`${u.weekly_hours}h`} />
            <InfoRow icon={Plane} label="Urlaubstage/Jahr" value={`${u.vacation_days_per_year}`} />
            {u.gross_salary_monthly > 0 && (
              <InfoRow icon={ShieldCheck} label="Bruttogehalt" value={`${u.gross_salary_monthly.toLocaleString("de-DE", { style: "currency", currency: "EUR" })} × ${u.salary_count}/Jahr`} />
            )}
            {u.contract_note && <div className="rounded-md border border-border/60 p-2 text-xs text-slate-600 dark:text-slate-300 whitespace-pre-wrap">{u.contract_note}</div>}
          </TabsContent>
          <TabsContent value="hours" className="pt-3">
            <div className="grid grid-cols-7 gap-1">
              {[["mo","Mo"],["di","Di"],["mi","Mi"],["do","Do"],["fr","Fr"],["sa","Sa"],["so","So"]].map(([k, lbl]) => (
                <div key={k} className="rounded-md border border-border/60 py-2 text-center">
                  <div className="text-[10px] font-bold text-slate-500">{lbl}</div>
                  <div className="font-heading font-bold text-sm mt-0.5">{u.work_hours?.[k] ?? 0}h</div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="entries" className="pt-3">
            <div className="max-h-[280px] overflow-y-auto divide-y divide-border/40">
              {detail.entries.length === 0 && <div className="text-sm text-slate-500 text-center py-4">Keine Einträge</div>}
              {detail.entries.map(e => (
                <div key={e.id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium">{new Date(e.date).toLocaleDateString("de-DE", { weekday: "short", day: "2-digit", month: "2-digit" })}</div>
                    <div className="text-xs text-slate-500">{e.clock_in?.slice(11,16)} – {e.clock_out?.slice(11,16) || "…"} · Pause {e.break_minutes || 0}m</div>
                  </div>
                  <div className="text-right">
                    <div className="font-heading font-bold">{((e.net_minutes || 0) / 60).toFixed(1)}h</div>
                    {e.overtime_minutes > 0 && <div className="text-[10px] text-amber-700">+{(e.overtime_minutes/60).toFixed(1)}h Ü.</div>}
                  </div>
                </div>
              ))}
            </div>
          </TabsContent>
          <TabsContent value="absences" className="pt-3">
            <div className="max-h-[280px] overflow-y-auto divide-y divide-border/40">
              {detail.absences.length === 0 && <div className="text-sm text-slate-500 text-center py-4">Keine Abwesenheiten</div>}
              {detail.absences.map(a => (
                <div key={a.id} className="py-2 flex items-center justify-between text-sm">
                  <div>
                    <div className="font-medium capitalize">{a.type}</div>
                    <div className="text-xs text-slate-500">{a.start_date} – {a.end_date} · {a.days || 0}T</div>
                    {a.reason && <div className="text-xs text-slate-400 italic">{a.reason}</div>}
                  </div>
                  <Badge variant={a.status === "approved" ? "default" : "secondary"} className={a.status === "approved" ? "bg-emerald-100 text-emerald-800 hover:bg-emerald-100" : ""}>{a.status}</Badge>
                </div>
              ))}
            </div>
          </TabsContent>
        </Tabs>

        <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-border/60">
          <Button data-testid="detail-edit-btn" size="sm" onClick={onEdit}><Pencil className="w-3.5 h-3.5 mr-1" /> Bearbeiten</Button>
          <Button data-testid="detail-toggle-btn" size="sm" variant="outline" onClick={onToggleActive}>
            {u.active ? <><Lock className="w-3.5 h-3.5 mr-1 text-amber-600" /> Sperren</> : <><Unlock className="w-3.5 h-3.5 mr-1 text-emerald-600" /> Entsperren</>}
          </Button>
          <Button data-testid="detail-delete-btn" size="sm" variant="ghost" onClick={onDelete} className="text-rose-600 hover:text-rose-700 ml-auto">
            <Trash2 className="w-3.5 h-3.5 mr-1" /> Löschen
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}

function Stat({ icon: Icon, label, value, sub }) {
  return (
    <div className="rounded-lg border border-border/60 p-3">
      <div className="text-[10px] uppercase tracking-wider text-slate-500 flex items-center gap-1"><Icon className="w-3 h-3" /> {label}</div>
      <div className="font-heading font-bold text-lg mt-0.5">{value}</div>
      {sub && <div className="text-xs text-slate-500">{sub}</div>}
    </div>
  );
}

function InfoRow({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2">
      <Icon className="w-3.5 h-3.5 text-slate-400 mt-0.5 shrink-0" />
      <div className="min-w-0 flex-1">
        <div className="text-[10px] uppercase tracking-wider text-slate-500">{label}</div>
        <div className="text-sm truncate">{value}</div>
      </div>
    </div>
  );
}
